import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ContainerTemplate from '../../components/ContainerTemplate';
import Checkbox from '../../components/Checkbox';
import Image from '../../components/Image';
import Input from '../../components/Input';
import Radio from '../../components/Radio';
import { v4 as v4uuid } from 'uuid';
import Select from '../../components/Select';
import Separator from '../../components/Separator';
import Switcher from '../../components/Switcher';
import Text from '../../components/Text';
import { pageSuccess } from '../../store/page/actions';
import useTrackRouteChangeHook from '../../utils/trackRouteChangeHook';
import { 
  setItem as setLocalCache,
 } from '../../utils/indexedDbHelper';
import merge from 'lodash.merge';
import { analyzeAndPrefetchImages } from '../../utils/prefetchHelper';
import { preloadImages } from '../../utils/preloadHelper';
import generateComponentStyles from '../../utils/styleHelper';
import { generateCssRule, filterDisplayProperty, 
  attachScriptsToHeader, attachFontsToHeader, replacePlaceholdersWithVariables, 
  formShimmerCacheKey, transformShimmerCache, parseOldFormat, parseNewFormat, updateDataWithModeUpdate } from '../../utils/base';
import { ApplicationState } from '../../store';
import { getGlobalVariables } from '../../utils/globalVariableHelper';
import { WebSocketFactory, WebSocketProvider, WebSocketMessage } from '../../services/websocket';

import { rowSizeGlobal, colCountGlobal } from '../../config/config';

import * as styles from './page.module.scss';
import { NodeTypeScheme } from '../../types/base';

const styleElementId = 'custom-css-header-kdlshHhgjhgf';

const componentMapping = {
  checkbox: Checkbox,
  image: Image,
  input: Input,
  radio: Radio,
  select: Select,
  separator: Separator,
  switcher: Switcher,
  text: Text,
};

const Page: React.FC = () => {
  const dispatch = useDispatch();
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [customFontLoaded, setCustomFontLoaded] = useState(false);
  const socketRef = useRef<WebSocketProvider | null>(null);
  const config = useSelector((state: ApplicationState) => state.config);
  const page = useSelector((state: ApplicationState) => state.page);
  const { data } = config || {};
  const { features } = data || {};
  const isProcesses = features?.processes || false;
  const { data: pageData2, settings, variableData, session_data, styleChunks, screenId } = page || {};
  const { deviceId, sessionId, hash, localShimmer } = session_data || {};
  
  // Get global variables from localStorage and assign to 'c' key
  const globalVariables = getGlobalVariables();
  const mergedVariables = merge(
    {}, 
    (settings?.variables || {}), 
    variableData || {},
    globalVariables || {},
  );
  
  const pageData = replacePlaceholdersWithVariables(pageData2, mergedVariables);
  const newSettings = { ...settings, variables: mergedVariables };
  const { page: pp } = pageData || {};

  useTrackRouteChangeHook(hash);

  useEffect(() => {
    const { basic, variables, classes } = data || {};
  
    if (basic && variables && classes) {
      const classKeys = Object.keys(classes);
      let styleElement = document.getElementById(styleElementId);
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleElementId;
        document.head.insertBefore(styleElement, document.head.firstChild);
      }
      const resulting = {};
      classKeys.forEach((k) => {
        const classStyles = filterDisplayProperty(classes[k]);
        resulting[k] = generateComponentStyles(classStyles);
      });
      const cssRules = Object.keys(resulting)
        .map((className) => generateCssRule(className, resulting[className]))
        .join('\n\n');
      styleElement.innerText = `${variables}\n${basic}\n${cssRules}`;
    }
  }, [data]);

  useEffect(() => {
    if (settings?.script) attachScriptsToHeader(settings?.script);
  }, [settings?.script]);

  useEffect(() => {
    if (!settings?.fonts) {
      // setFontsLoaded(true);
    };
    if (settings?.fonts) {
      attachFontsToHeader(settings?.fonts).then(() => {
       setTimeout(function() {
        // setFontsLoaded(true);
       }, 0); 
      }).catch((error) => {
        console.error('Font loading failed:', error);
        // setFontsLoaded(true); 
        // Fallback to show content even if fonts fail to load
      });
    } else {
      // setFontsLoaded(true); 
      // No fonts to load, show content immediately
    }
  }, [settings?.fonts]);

  useEffect(() => {
    if (settings?.page) {
      const { title, description, icon } = settings.page;
      if (title) {
        document.title = title;
      }
      if (description) {
        let metaDescription = document.querySelector("meta[name='description']") as HTMLMetaElement;
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          metaDescription.content = description;
          document.getElementsByTagName('head')[0].appendChild(metaDescription);
        }
      }
      if (icon) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = icon;
      }
    }
  }, [settings]);

  const handlePageUpdate = useCallback((message: WebSocketMessage) => {
    if (message.type === 'pageUpdate' && message.data) {
      const {  data: dataDraft } = message.data || {};
      const data = dataDraft || message.data || {};
      const { page: pp, settings: settingsNew, variableData, screenId } = data.sections ? parseOldFormat(data) : parseNewFormat(data);
      
      // Handle new update mode scheme for WebSocket updates
      let finalPageData = pp;
      let finalVariableData = variableData;
      let shouldUpdateCache = false;
      
      if (data.mode === 'update') {
        const currentPageData = page?.data?.page;
        const currentVariableData = page?.variableData;
        
        const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
          updateDataWithModeUpdate(currentPageData, data, currentVariableData);
        
        finalPageData = updatedPage;
        finalVariableData = updatedVariableData;
        shouldUpdateCache = cacheFlag;
      }
      
      if (settingsNew?.command) {
        const pathname = (settingsNew?.command === '/') ? '/' : settingsNew?.command.replace(/\/$/, '');
        // Correct the route if it ends with a trailing slash without triggering a location change
        if (pathname.length) {
          window.history.replaceState(null, '', pathname);
        }
      }
      
      dispatch(pageSuccess({ 
        data: finalPageData, 
        settings: { ...settings, ...settingsNew },
        session_data: {
          ...session_data,
          hash: `skip-s-${v4uuid()}`,
        }, 
        variableData: finalVariableData,
        screenId,
       }));
       
      if (message.data?.requestData && message.data?.requestData.component_id && message.data?.requestData.screen_id) {
        const { command, action, component_id: componentId, screen_id: screenId } = message.data?.requestData;
        const cacheKey = formShimmerCacheKey({ command, action, component_id: componentId, screen_id: screenId });
        const { status, transformed } = transformShimmerCache(data?.screen?.sections);

        // Handle caching based on mode and settings
        if (data.mode === 'update') {
          // For update mode, save the final transformed data
          if (shouldUpdateCache) {
            setLocalCache(cacheKey, {
              ...data,
              screen: {
                ...data.screen,
                sections: finalPageData,
              },
              variable_data: finalVariableData,
            });
          }

        } else {
          if (status && transformed) {
            setLocalCache(cacheKey, {
              ...data,
              screen: {
                ...data.screen,
                sections: transformed,
              },
              variable_data: variableData,
            });
          }
        }
      }
    }
  }, [dispatch, session_data, page]);

  const connectWebSocket = useCallback(() => {
    if (!deviceId || !sessionId || socketRef.current) return;

    const provider = isProcesses ? 'native' : 'socket.io';
    // Create WebSocket provider using factory
    // You can change 'socket.io' to 'native' to use the Golang WebSocket service
    const wsProvider = WebSocketFactory.createProvider(provider, {
      deviceId,
      sessionId,
      onPageUpdate: handlePageUpdate,
      onConnect: () => {
        console.log('WebSocket Connected');
      },
      onDisconnect: (reason) => {
        console.log('WebSocket Disconnected', reason);
      },
      onError: (error) => {
        console.error('WebSocket Error:', error);
      },
    });

    wsProvider.connect();
    socketRef.current = wsProvider;

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [deviceId, sessionId, handlePageUpdate]);

  useEffect(() => {
    
    if (!isProcesses && deviceId && sessionId) {
      connectWebSocket();
    }
    // If live_updates = true and credentials are available
    else if (settings?.live_updates && deviceId && sessionId) {
      // Close old connection before establishing new one
      if (socketRef.current) {
        //skip as it's already connected
        return;
      }
      // Connect new WebSocket
      connectWebSocket();
    } 
    // If live_updates = false and WS is connected → disconnect
    else if (!settings?.live_updates && socketRef.current && deviceId && sessionId) {
      socketRef.current.setManualDisconnect(true); // Notify provider this is a manual disconnect
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, [settings?.live_updates, deviceId, sessionId, connectWebSocket]);

  // useEffect(() => {
  //   if (document.fonts.check('1em Inter')) {
  //     setCustomFontLoaded(true);
  //   } else {
  //     document.fonts.ready.then(() => {
  //       if (document.fonts.check('1em Inter')) {
  //         setCustomFontLoaded(true);
  //       }
  //     });
  //   }
  // }, []);

  useEffect(() => {
    if (!pp) return;
    const url = analyzeAndPrefetchImages(pp);
    preloadImages(url);
  }, [pp]);

  // if (!fontsLoaded || !customFontLoaded) {
  //   return <div style={{ color: 'transparent', fontFamily: 'Inter', fontWeight: 600 }}>Loading <span style={{ color: 'transparent', fontFamily: 'Inter', fontWeight: 400 }}>...</span></div>;
  // }

  return (
    <div className={styles.page} style={generateComponentStyles(settings?.style)}>
      <div className={styles.pageWrap}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${settings?.cols || colCountGlobal}, 1fr)`,
        gridAutoRows: settings?.rows ? undefined : `${rowSizeGlobal}px`,
        gridTemplateRows: settings?.rows ? `repeat(${settings?.rows}, 1fr)` : undefined,
      }}
      >
        {(typeof pp === 'object' && Object.keys(pp) && !Array.isArray(pp)) ? (
          <ContainerTemplate 
            {...pp as NodeTypeScheme} 
            pageSettings={newSettings}
            styleChunks={styleChunks}
            topLayerNode={true}
            hash={hash}
            enableLocalShimmer={localShimmer}
          />
        ) : null}

        {Array.isArray(pp) ? (
          <>
            {pp.map((item) => {
              const Component = componentMapping[item.component];
              if (Component) {
                return <Component key={item.id} {...item} pageSettings={newSettings} styleChunks={styleChunks} topLayerNode={true} enableLocalShimmer={localShimmer} />;
              } else if (item.component === 'container' || item.component === 'table') {
                return <ContainerTemplate 
                key={item.id} 
                {...(item as NodeTypeScheme)} 
                pageSettings={newSettings} 
                styleChunks={styleChunks} 
                topLayerNode={true}
                hash={hash}
                enableLocalShimmer={localShimmer}
                />;
              }
              return null;
            })}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Page;